import React from 'react';
import { Linkedin, Github, Download, ArrowDown, Hand, GraduationCap, Briefcase, Heart, Target, Mail, Phone, MapPin } from 'lucide-react';

const Hero = () => {
  const scrollToExperience = () => {
    const element = document.querySelector('#experience');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="min-h-screen flex items-center justify-center section-padding pb-16 sm:pb-12 lg:pb-16 relative">
      <div className="container-max w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-10 lg:gap-16 items-center max-w-5xl mx-auto">

          {/* Left: Photo */}
          <div className="flex justify-center animate-fade-in">
            <div className="relative">
              {/* Animated green border: sweeps left → right on load */}
              <div className="absolute inset-0 rounded-2xl border-8 border-primary-700 animate-border-sweep pointer-events-none" />
              <div className="w-36 h-48 sm:w-48 sm:h-64 md:w-72 md:h-96 rounded-2xl overflow-hidden">
                <img
                  src={`${process.env.PUBLIC_URL}/laxman-photo.jpeg`}
                  alt="Laxman Singh Rawat"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div>
            {/* Greeting */}
            <div className="animate-fade-in mb-6">
              <p className="text-primary-500 text-lg font-medium flex items-center gap-2 animate-slide-up">
                <Hand size={20} className="text-primary-500 animate-wave" />
                Hey there! I'm
              </p>
            </div>

            {/* Name */}
            <h1 className="text-4xl md:text-5xl lg:text-5xl font-black mb-4 tracking-tight animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <span className="gradient-text">Laxman </span>
              <span className="text-gsap-text">Singh Rawat</span>
            </h1>

            {/* Title */}
            <p className="text-primary-500 text-sm font-semibold tracking-[0.2em] uppercase mb-8 animate-slide-up" style={{ animationDelay: '0.15s' }}>
              Solution Architect &amp; Full Stack Developer
            </p>

            {/* About Me Header */}
            <p className="text-lg text-gsap-muted mb-6 animate-slide-up" style={{ animationDelay: '0.18s' }}>
              A bit about me
            </p>

            {/* About Me - Flowing Text */}
            <div className="space-y-4 mb-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <p className="text-base text-gsap-muted leading-relaxed flex items-start gap-3">
                <GraduationCap size={20} className="text-primary-500 flex-shrink-0 mt-0.5" />
                <span>
                  Pursuing my <span className="text-primary-500 font-medium">Master's in Computer Science</span> at <span className="text-gsap-text font-medium">NYU</span>,
                  with a B.Tech from <span className="text-gsap-text font-medium">IIT Kharagpur</span>.
                </span>
              </p>

              <p className="text-base text-gsap-muted leading-relaxed flex items-start gap-3">
                <Briefcase size={20} className="text-primary-500 flex-shrink-0 mt-0.5" />
                <span>
                  Interned at <span className="text-gsap-text font-medium">Amazon</span> shipping production <span className="text-primary-500 font-medium">RAG</span> and AI systems; <span className="text-primary-500 font-medium">5+ years</span> at <span className="text-gsap-text font-medium">AB InBev</span> building enterprise platforms at scale.
                </span>
              </p>

              <p className="text-base text-gsap-muted leading-relaxed flex items-start gap-3">
                <Target size={20} className="text-primary-500 flex-shrink-0 mt-0.5" />
                <span>
                  Focused on <span className="text-primary-500 font-medium">AI/ML</span>, <span className="text-primary-500 font-medium">cloud architecture</span>, and <span className="text-primary-500 font-medium">data visualization</span>.
                </span>
              </p>

              <p className="text-base text-gsap-muted leading-relaxed flex items-start gap-3">
                <Heart size={20} className="text-primary-500 flex-shrink-0 mt-0.5" />
                <span>
                  When not coding, you can find me <span className="text-primary-500 font-medium">sketching and illustrating</span>, <span className="text-primary-500 font-medium">
                    learning new recipes</span>, or <span className="text-primary-500 font-medium">competitive gaming</span>.
                </span>
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8 animate-slide-up" style={{ animationDelay: '0.3s' }}>
              <a
                href={`${process.env.PUBLIC_URL}/Laxman-Singh-Rawat-Resume.pdf`}
                download="Laxman-Singh-Rawat-Resume.pdf"
                className="px-8 py-4 bg-primary-500 hover:bg-primary-600 text-gsap-bg font-semibold rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/25 flex items-center justify-center gap-2"
              >
                <Download size={18} />
                Resume
              </a>
            </div>

            {/* Social + Contact Links */}
            <div className="flex items-center gap-3 flex-wrap animate-slide-up" style={{ animationDelay: '0.4s' }}>
              <a
                href="https://linkedin.com/in/laxman-s-rawat"
                target="_blank"
                rel="noopener noreferrer"
                title="LinkedIn"
                className="w-10 h-10 rounded-full border border-gsap-border flex items-center justify-center text-gsap-muted hover:text-primary-500 hover:border-primary-500 transition-all duration-300"
              >
                <Linkedin size={18} />
              </a>

              <a
                href="https://github.com/LaxmanSRawat"
                target="_blank"
                rel="noopener noreferrer"
                title="GitHub"
                className="w-10 h-10 rounded-full border border-gsap-border flex items-center justify-center text-gsap-muted hover:text-primary-500 hover:border-primary-500 transition-all duration-300"
              >
                <Github size={18} />
              </a>

              <a
                href="mailto:laxman.sr.iitkgp@gmail.com"
                title="Email"
                className="w-10 h-10 rounded-full border border-gsap-border flex items-center justify-center text-gsap-muted hover:text-primary-500 hover:border-primary-500 transition-all duration-300"
              >
                <Mail size={18} />
              </a>

              <a
                href="tel:+19294091436"
                title="Phone: +1 929-409-1436"
                className="w-10 h-10 rounded-full border border-gsap-border flex items-center justify-center text-gsap-muted hover:text-primary-500 hover:border-primary-500 transition-all duration-300"
              >
                <Phone size={18} />
              </a>

              {/* Separator */}
              <span className="text-gsap-border select-none mx-1">|</span>

              {/* Location */}
              <span className="flex items-center gap-1.5 text-gsap-muted text-sm">
                <MapPin size={14} className="text-primary-500 flex-shrink-0" />
                New York, NY
              </span>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={scrollToExperience}
          className="absolute bottom-4 sm:bottom-12 left-1/2 transform -translate-x-1/2 text-gsap-muted hover:text-primary-500 transition-colors duration-300 animate-bounce-slow"
        >
          <ArrowDown size={24} />
        </button>
      </div>
    </section>
  );
};

export default Hero;
